import { type MenuVirtualTriggerProps, useLayoutContext } from "@overmap-ai/blocks"
import { type Issue, type IssueType, type Stored, EMPTY_ARRAY } from "@overmap-ai/common"
import {
	selectIssueTypeById,
	selectIssueTypeIdentifiersOfIssueType,
	selectIssueTypePrioritiesOfIssueType,
	selectIssueTypeStatusesOfIssueType,
	selectLatestIssueTypeFieldsOfIssueType,
	selectUsersOfProject,
} from "@overmap-ai/core"
import { useNavigate } from "@tanstack/react-router"
import { memo, useCallback, useMemo, useState } from "react"
import type { MarkerEvent } from "react-map-gl/mapbox"

import { useDeserializeFieldSchema } from "../../components/Form/hooks"
import { IssueMarkerContextMenu } from "../../components/MapContextMenus/IssueMarkerContextMenu"
import { useFilterManager } from "../../filtering/hooks"
import { getIssueDataFilterSchema } from "../../filtering/schemas"
import { useIssueData } from "../../hooks/issues"
import { SLIDE_OUT_IDS } from "../../layouts"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { selectActiveProjectId, selectIsIssueTypeHidden, selectIssueTypeMapFiltersById } from "../../store/selectors"
import { selectIssuesOfProjectAndIssueType } from "../../store/selectors/issues"
import type { IssueData, ModelAnnotatedEventHandler } from "../../typings"
import { useCurrentMapEvent } from "../hooks"
import { IssueMarker } from "./IssueMarker"

interface IssueMarkersProps {
	issueTypeId: IssueType["uuid"]
	activeIssueId: Issue["uuid"] | null
}

export const IssueMarkers = memo<IssueMarkersProps>((props) => {
	const { issueTypeId, activeIssueId } = props

	const issueType = useAppSelector(selectIssueTypeById(issueTypeId))

	if (!issueType) {
		throw new Error(`Issue type ${issueTypeId} not found`)
	}

	const activeProjectId = useAppSelector(selectActiveProjectId)!
	const isHidden = useAppSelector(selectIsIssueTypeHidden(issueType.uuid))
	const latestIssueTypeFields = useAppSelector(selectLatestIssueTypeFieldsOfIssueType(issueType.uuid))
	const issueTypeIdentifiers = useShallowEqaulSelector(selectIssueTypeIdentifiersOfIssueType(issueType.uuid))
	const issueTypePriorities = useShallowEqaulSelector(selectIssueTypePrioritiesOfIssueType(issueType.uuid))
	const issueTypeStatuses = useShallowEqaulSelector(selectIssueTypeStatusesOfIssueType(issueType.uuid))
	const serializedFilters = useAppSelector(selectIssueTypeMapFiltersById(issueType.uuid))
	const users = useShallowEqaulSelector(selectUsersOfProject(activeProjectId))
	const navigate = useNavigate()
	const { setOpen } = useLayoutContext()

	const fieldSchema = useDeserializeFieldSchema(latestIssueTypeFields?.fields ?? EMPTY_ARRAY)

	const issuesOfType = useShallowEqaulSelector(selectIssuesOfProjectAndIssueType(activeProjectId, issueTypeId))
	const issueData = useIssueData(issuesOfType)

	const filterSchema = useMemo(
		() =>
			getIssueDataFilterSchema({
				assignees: users,
				issueTypeIdentifiers,
				issueTypeFieldSchema: fieldSchema,
				issueTypePriorities,
				issueTypeStatuses,
				users,
			}),
		[fieldSchema, issueTypeIdentifiers, issueTypePriorities, issueTypeStatuses, users],
	)

	const filters = useMemo(() => filterSchema.deserializeFilters(serializedFilters), [filterSchema, serializedFilters])

	const filterManager = useFilterManager<IssueData>({
		filterSchema,
		filters,
	})

	const issues = useMemo(() => {
		if (isHidden) return []

		return filterManager.applyFilters(filters, issueData).map((issue) => issue.issue)
	}, [filterManager, filters, isHidden, issueData])

	const [issueMarkerVirtualElement, setIssueMarkerVirtualElement] =
		useState<MenuVirtualTriggerProps["virtualElement"]>(null)
	const [contextMenuIssue, setContextMenuIssue] = useState<Stored<Issue> | null>(null)

	const handleIssueMarkerClick = useCallback<ModelAnnotatedEventHandler<MarkerEvent<MouseEvent>, Stored<Issue>>>(
		({ model: issue, event }) => {
			event.originalEvent.stopPropagation()
			event.originalEvent.preventDefault()

			void navigate({
				from: "/$organizationId/$projectId",
				to: "/$organizationId/$projectId",
				search: { issue: issue.uuid },
			})
			setOpen(SLIDE_OUT_IDS.RIGHT, true)
		},
		[navigate, setOpen],
	)

	const handleIssueMarkerContextMenu = useCallback<
		ModelAnnotatedEventHandler<MarkerEvent<MouseEvent>, Stored<Issue>>
	>(({ event, model: issue }) => {
		const { target, originalEvent } = event

		originalEvent.preventDefault()
		originalEvent.stopPropagation()

		setContextMenuIssue(issue)
		setIssueMarkerVirtualElement({
			getBoundingClientRect() {
				return target.getElement().getBoundingClientRect()
			},
		})
	}, [])

	const handleContextMenuClose = useCallback(() => {
		setContextMenuIssue(null)
		setIssueMarkerVirtualElement(null)
	}, [])

	const handleIssueMarkerContextMenuDelete = useCallback(async () => {
		await navigate({
			from: "/$organizationId/$projectId",
			to: "/$organizationId/$projectId",
			search: { issue: undefined },
			replace: true,
		})
		setOpen(SLIDE_OUT_IDS.RIGHT, false)
		handleContextMenuClose()
	}, [handleContextMenuClose, navigate, setOpen])

	useCurrentMapEvent("zoomstart", handleContextMenuClose)

	return (
		<>
			{issues.map((issue) => (
				<IssueMarker
					active={issue.uuid === activeIssueId}
					issue={issue}
					key={issue.uuid}
					onClick={handleIssueMarkerClick}
					onContextMenu={handleIssueMarkerContextMenu}
				/>
			))}
			{contextMenuIssue && (
				<IssueMarkerContextMenu
					issueId={contextMenuIssue.uuid}
					virtualElement={issueMarkerVirtualElement}
					onDelete={handleIssueMarkerContextMenuDelete}
					side="right"
					align="start"
				/>
			)}
		</>
	)
})
IssueMarkers.displayName = "IssueMarkers"
